/**
 * Data hooks for the Client-Email Review subpage (ADR-0115).
 *
 *   useClientEmails(firmId) — GET /api/firm-success/firms/:id/comms-templates.
 *     The per-firm review payload: one diff per email (standard / live /
 *     pending + which fields changed). No poll — it's a deliberate
 *     destination reached from the dashboard item or the firm panel, not a
 *     watch surface; it refetches on focus (global default) and after an
 *     approve via invalidation. Disabled until a firmId is present.
 *
 *   useApplyCommsTemplates() — POST …/comms-templates/apply (no body; the
 *     blob to promote is whatever the firm staged). The "approve" step:
 *     promotes pending → live and clears staging server-side. On success,
 *     invalidates this firm's review query (now browse mode) AND the
 *     dashboard queries (the derived review item is gone — Slice 3).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FirmCommsTemplatesViewResponseSchema,
  FirmCommsTemplatesApplyResponseSchema,
  type FirmCommsTemplatesViewResponse,
  type FirmCommsTemplatesApplyResponse,
} from "@command-center/shared";
import { apiGet, apiPost } from "./api";

export const CLIENT_EMAILS_KEY = "client-emails";

export function clientEmailsKey(firmId: string) {
  return [CLIENT_EMAILS_KEY, firmId] as const;
}

export function useClientEmails(firmId: string) {
  return useQuery<FirmCommsTemplatesViewResponse, Error>({
    queryKey: clientEmailsKey(firmId),
    enabled: firmId.length > 0,
    queryFn: async () => {
      const path = `/api/firm-success/firms/${encodeURIComponent(
        firmId,
      )}/comms-templates`;
      const raw = await apiGet<unknown>(path);
      const parsed = FirmCommsTemplatesViewResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `${path} failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
  });
}

export function useApplyCommsTemplates() {
  const queryClient = useQueryClient();
  return useMutation<FirmCommsTemplatesApplyResponse, Error, { firmId: string }>(
    {
      mutationFn: async ({ firmId }) => {
        const path = `/api/firm-success/firms/${encodeURIComponent(
          firmId,
        )}/comms-templates/apply`;
        const raw = await apiPost<unknown>(path, {});
        const parsed = FirmCommsTemplatesApplyResponseSchema.safeParse(raw);
        if (!parsed.success) {
          throw new Error(
            `${path} failed schema validation: ${parsed.error.message}`,
          );
        }
        return parsed.data;
      },
      onSuccess: (_result, vars) => {
        void queryClient.invalidateQueries({
          queryKey: clientEmailsKey(vars.firmId),
        });
        void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
        void queryClient.invalidateQueries({ queryKey: ["dashboard-count"] });
      },
    },
  );
}
